import { z } from 'zod'

import {
  ChannelStatusTypes,
  ConversationFilterType,
  HandoverErrorType,
  LiveChatBulkActionTypes,
  SelectionType,
  StatusFilterType,
  ThreadsSortingTypes,
} from 'apps/chat/constants/LiveChatInterfaces'
import { LiveChatNoteTypes } from 'apps/chat/constants/LiveChatNoteTypes'
import { ThreadStatus } from 'apps/chat/interfaces/statuses'
import { ChatMessageBlockType } from 'apps/chat/types/chatMessagesBlockList'
import { ChatMessageType } from 'apps/chat/types/chatMessageTypeList'
import { StatusType } from 'common/audience/models/Subscriber'
import { ChannelType } from 'common/core/constants/ChannelType'

import { ReducedUserSchema } from '../../audience/schemas'
import { filterDataTypeSchema } from '../filter/schemas'
import { fileAttachmentData, LabelSchema } from '../schemas'

import {
  AssignmentErrorSchema,
  BulkActionInProgressErrorSchema,
  CurrentThreadUserErrorSchema,
} from './errors'

export const threadAssignmentSchema = z.object({
  user_id: z.number(),
  name: z.string(),
  avatar: z.string().nullable(),
})

export const liveChatChannelSchema = z.object({
  enabled: z.boolean(),
  opt_in: z.boolean(),
  type: z.enum(ChannelType),
  status: z.enum(ChannelStatusTypes),
})

export const senderSchema = z.object({
  user_id: z.number(),
  user_name: z.string(),
  avatar: z.string(),
  profile_id: z.number().optional(),
})

export const noteImageSchema = z.object({
  caid: z.number(),
  image_size: z.object({ width: z.number(), height: z.number(), size: z.number() }),
  img_big: z.string(),
  img_original: z.string().optional(),
  img_small: z.string(),
  sign: z.string(),
  title: z.string(),
  type: z.literal(ChatMessageBlockType.IMAGE),
})

export const noteMessageSchema = z.object({
  _oid: z.string(),
  type: z.union([z.literal(ChatMessageBlockType.TEXT), z.literal(ChatMessageBlockType.ATTACHMENT)]),
  content: z.object({
    text: z.string().optional(),
    type: z.string().optional(),
    data: z.union([noteImageSchema, fileAttachmentData]).optional(),
  }),
})

export const liveChatNoteSchema = z.object({
  message_id: z.string(),
  model: z.object({
    _oid: z.string(),
    messages: z.array(noteMessageSchema).optional(),
    sender: senderSchema.optional(),
    mentions: z.array(z.number()).nullish(),
  }),
  page_id: z.string(),
  sort_id: z.number(),
  timestamp: z.number(),
  type: z.enum(LiveChatNoteTypes),
  user_id: z.string(),
})

export const chatThreadUserSchema = z.object({
  // IDs
  user_id: z.string(),
  fb_psid: z.string().nullable(),
  wa_id: z.string().nullable(),
  ig_id: z.number().nullable(),
  tt_user_id: z.string().nullable(),
  source_app_id: z.number().nullable(),

  // Timestamps
  ts_added: z.number(),
  raw_ts_added: z.number(),
  ts_subscribed: z.number(),
  ts_automation_paused_until: z.number().nullable(),
  ts_automation_paused_forever: z.boolean().nullable(),
  ts_wa_free_entry_point_until: z.number().nullable(),
  ts_wa_customer_service_window_until: z.number().nullable(),

  // Basic user info
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  name: z.string().nullable(),
  title: z.string().nullable(),
  avatar: z.string().nullable(),
  gender: z.string().nullable(),
  phone: z.string().nullable(),
  phone_country_code: z.string().nullable(),
  email: z.string().nullable(),
  optin_source: z.number().nullable(),
  ig_username: z.string().nullable(),
  tg_username: z.string().nullable(),
  tt_username: z.string().nullable(),
  tt_display_name: z.string().nullable(),
  is_us_wa_number: z.boolean().nullable(),

  // Statuses
  status: z.enum(StatusType),
  fb_status: z.enum(StatusType),
  tg_status: z.enum(StatusType),
  ig_status: z.enum(StatusType),
  wa_status: z.enum(StatusType),
  tt_status: z.enum(StatusType),

  // Opt-in flags
  optin_phone: z.boolean().nullable(),
  optin_email: z.boolean().nullable(),
  optin_ig: z.boolean().nullable(),
  optin_tg: z.boolean().nullable(),
  optin_fb: z.boolean().nullable(),
  optin_wa: z.boolean().nullable(),
  optin_tt: z.boolean().nullable(),
  has_question_context: z.boolean(),

  // Additional fields
  tg_importing: z.boolean().nullable(),
  phone_us_state: z.string().nullable(),
  labels: z.array(LabelSchema).optional(),
})

export const lastLcEventSchema = z.object({
  message_id: z.string(),
  page_id: z.string(),
  type: z.enum(ChatMessageType),
  user_id: z.string(),
  sort_id: z.number(),
  timestamp: z.number(),
  model: z.looseObject({}),
})

const ExtendedChannelType = {
  ...ChannelType,
  NOTIFY_ADMIN: 'notify_admin',
} as const

export const threadSchema = z.object({
  timestamp: z.number(),

  page_id: z.string(),
  user_id: z.string(),
  sort_id: z.number(),

  lc_thread_status: z.enum(ThreadStatus),
  /** is last message read by subscriber */
  lc_thread_read: z.boolean(),
  /** is last message read by user */
  lc_thread_user_read: z.boolean(),

  /** 24-hour window */
  messaging_is_limited: z.boolean(),

  last_lc_read_by_user_event_id: z.number().nullable(),
  last_interaction_fb: z.string().nullable(),
  last_lc_event_channel: z.enum(ExtendedChannelType).nullable(),
  last_lc_event: lastLcEventSchema.nullable(),

  user: chatThreadUserSchema,
  assignment: threadAssignmentSchema.nullable(),
  channels: z.array(liveChatChannelSchema),
})

export const threadPartialDetailsSchema = threadSchema.pick({
  timestamp: true,
  page_id: true,
  user_id: true,
  sort_id: true,
  lc_thread_status: true,
  lc_thread_read: true,
  lc_thread_user_read: true,
  messaging_is_limited: true,
  last_lc_read_by_user_event_id: true,
  last_interaction_fb: true,
  last_lc_event_channel: true,
  last_lc_event: true,
  assignment: true,
  user: true,
})

export const bulkActionRequestSchema = z.object({
  type: z.enum(LiveChatBulkActionTypes),
  ids: z.array(z.string()),
  selection_type: z.enum(SelectionType),
  filter: filterDataTypeSchema,
  status: z.enum(StatusFilterType),
  folder: z.enum(ConversationFilterType),
  folder_user_id: z.number().nullable(),
  async: z.string().optional(),
  assigned_user_id: z.number().optional(),
})

export const threadsFetchRequestSchema = z.object({
  limit: z.number(),
  sorting: z.enum(ThreadsSortingTypes),
  filter: filterDataTypeSchema,
  status: z.enum(StatusFilterType),
  folder: z.enum(ConversationFilterType),
  folder_user_id: z.number().nullable(),
})

export const threadsFetchResponseSchema = z.object({
  limiter: z.number().nullable(),
  total_threads_num: z.number(),
  threads: z.array(threadSchema),
})

export const hopErrorSchema = z.object({
  error_data: z.object({
    hop_error: z.object({
      type: z.enum(HandoverErrorType),
      counter: z.number(),
    }),
  }),
})

export const currenThreadFetchResponseSchema = z.object({
  thread: threadSchema,
  notes: z.array(liveChatNoteSchema).nullable(),
  hop_error: hopErrorSchema.nullable(),
})

export const threadsSchemas = {
  fetch: {
    query: z
      .object({
        limiter: z.number().optional(),
      })
      .optional(),
    path: z.undefined(),
    request: threadsFetchRequestSchema,
    response: threadsFetchResponseSchema,
  },
  fetchThread: {
    query: z.object({ user_id: z.string() }),
    path: z.undefined(),
    request: z.undefined(),
    response: currenThreadFetchResponseSchema,
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  bulk: {
    query: z.undefined(),
    path: z.undefined(),
    request: bulkActionRequestSchema,
    response: z.object({
      async: z.string(),
    }),
    errors: {
      BulkActionInProgressError: BulkActionInProgressErrorSchema,
    },
  },
  checkThreadSatisfiesFilter: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      subscriber_id: z.string(),
      filter: filterDataTypeSchema,
    }),
    response: z.object({
      satisfy: z.boolean(),
    }),
  },
  loadThreadPartialDetails: {
    query: z.object({ subscriber_id: z.string(), with_labels: z.boolean().optional() }),
    path: z.undefined(),
    request: z.undefined(),
    response: z.object({
      thread: threadPartialDetailsSchema,
    }),
  },
  takeThreadControl: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      subscriber_id: z.string(),
    }),
    response: z.undefined(),
  },
  closeAllThreads: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.undefined(),
    response: z.object({
      thread_list: z.array(z.number()),
    }),
    errors: {
      BulkActionInProgressError: BulkActionInProgressErrorSchema,
    },
  },
  updateThreadStatus: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
      thread_status: z.enum(ThreadStatus),
    }),
    response: z.object({
      thread: threadSchema,
    }),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  setAssignment: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      assigned_user_id: z.number().or(z.string()).nullable(),
      user_id: z.string(),
    }),
    response: z.object({
      thread: threadSchema,
    }),
    errors: {
      AssignmentError: AssignmentErrorSchema,
    },
  },
  markThreadAsRead: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.undefined(),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  markThreadAsUnread: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.undefined(),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  setAutomationPauseForever: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  setAutomationPause: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
      delay: z.number(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
  clearPause: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
    errors: {
      CurrentThreadUserError: CurrentThreadUserErrorSchema,
    },
  },
}

export type LiveChatThread = z.infer<typeof threadSchema>
export type LiveChatThreadUser = z.infer<typeof chatThreadUserSchema>
export type ThreadsFetchRequestBody = z.infer<typeof threadsFetchRequestSchema>
export type ThreadsFetchResponse = z.infer<typeof threadsFetchResponseSchema>
export type CurrentThreadFetchResponse = z.infer<typeof currenThreadFetchResponseSchema>
